import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * AreaGroupDrawer — slide-out drawer for Add / Edit area group.
 * Includes a searchable area picker for area assignment.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   group: object | null,           // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   onDelete: (group: object) => void,
 *   areas: Array<{ area_id: number, area_name: string, area_type_description: string }>
 * }} props
 */
const AreaGroupDrawer = ({ isOpen, onClose, group, onSave, onDelete, areas, canManageGroups = true }) => {
  const isEditMode = !!group;
  const drawerRef = useRef(null);
  // When the user lacks group-management rights, the drawer collapses to an
  // "edit area assignments" experience: name/description are read-only and the
  // Delete action is hidden.
  const metadataDisabled = !canManageGroups;

  // Form state
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [areaSearch, setAreaSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  // Populate form on open/group change
  useEffect(() => {
    if (isOpen && group) {
      setGroupName(group.area_group_name || '');
      setDescription(group.area_group_description || '');
      setSelectedAreaIds(
        (group.areas || []).map((a) => a.area_id?.toString() || a.toString())
      );
      setAreaSearch('');
      setShowSelectedOnly(false);
    } else if (isOpen && !group) {
      setGroupName('');
      setDescription('');
      setSelectedAreaIds([]);
      setAreaSearch('');
      setShowSelectedOnly(false);
    }
  }, [isOpen, group]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Filter areas by search and "show selected" toggle
  const filteredAreas = useMemo(() => {
    let list = areas || [];
    if (showSelectedOnly) {
      list = list.filter((a) => selectedAreaIds.includes(a.area_id.toString()));
    }
    if (areaSearch.trim()) {
      const s = areaSearch.toLowerCase();
      list = list.filter(
        (a) =>
          a.area_name?.toLowerCase().includes(s) ||
          a.area_type_description?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [areas, areaSearch, showSelectedOnly, selectedAreaIds]);

  const handleAreaToggle = useCallback((areaId) => {
    const aStr = areaId.toString();
    setSelectedAreaIds((prev) =>
      prev.includes(aStr) ? prev.filter((id) => id !== aStr) : [...prev, aStr]
    );
  }, []);

  const handleSelectAllAreas = useCallback(() => {
    const allFilteredIds = filteredAreas.map((a) => a.area_id.toString());
    setSelectedAreaIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
  }, [filteredAreas]);

  const handleClearAreas = useCallback(() => {
    setSelectedAreaIds([]);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);

      const formData = {
        area_group_id: group?.area_group_id || null,
        area_group_name: groupName.trim(),
        area_group_description: description.trim(),
        area_ids: selectedAreaIds.map((id) => parseInt(id, 10)),
      };

      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    },
    [groupName, description, selectedAreaIds, group, onSave]
  );

  /* Hands off to the page's typed confirmation — see AreaDrawer for why the
     inline footer confirmation was removed. */
  const handleDeleteClick = useCallback(() => {
    if (group && onDelete) onDelete(group);
  }, [group, onDelete]);

  const isFormValid = groupName.trim();

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      classPrefix="ap"
      size="lg"
      panelRef={drawerRef}
      ariaLabel={isEditMode ? 'Edit Area Group' : 'New Area Group'}
      title={isEditMode ? 'Edit Area Group' : 'New Area Group'}
      rawBody
    >
      {/* Scrollable form body */}
      <form onSubmit={handleSubmit} className="ap-form">
        <div className="ap-form__body">
          {/* Group Name */}
          <div>
            <label htmlFor="drawer-group-name" className="ap-field__label">
              Group Name <span className="ap-field__required">*</span>
            </label>
            <input
              type="text"
              id="drawer-group-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              required
              className="ap-field__input"
              placeholder="Enter group name"
              disabled={metadataDisabled}
              title={metadataDisabled ? 'You do not have permission to edit group name' : undefined}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="drawer-group-description" className="ap-field__label">
              Description
            </label>
            <textarea
              id="drawer-group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="ap-field__textarea"
              placeholder="Optional description..."
              rows={3}
              disabled={metadataDisabled}
              title={metadataDisabled ? 'You do not have permission to edit description' : undefined}
            />
          </div>

          {/* Area Picker */}
          <div className="ap-area-picker">
            <div className="ap-area-picker__header">
              <p className="ap-field__label" style={{ marginBottom: 0 }}>
                Areas
                <span className="ap-area-picker__count">
                  ({selectedAreaIds.length} selected)
                </span>
              </p>
            </div>

            {/* Search */}
            <div className="ap-area-picker__search">
              <i className="fe fe-search ap-area-picker__search-icon" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search areas..."
                value={areaSearch}
                onChange={(e) => setAreaSearch(e.target.value)}
                className="ap-area-picker__search-input"
              />
            </div>

            {/* Select All / Clear / Show Selected */}
            <div className="ap-area-picker__actions">
              <button
                type="button"
                onClick={handleSelectAllAreas}
                className="ap-area-picker__action-btn"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearAreas}
                className="ap-area-picker__action-btn"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setShowSelectedOnly((prev) => !prev)}
                className={`ap-area-picker__action-btn${showSelectedOnly ? ' ap-area-picker__action-btn--active' : ''}`}
              >
                Show Selected
              </button>
            </div>

            {/* Area list */}
            <div className="ap-area-picker__list">
              {filteredAreas.length === 0 ? (
                <p className="ap-area-picker__empty">
                  {showSelectedOnly ? 'No selections.' : 'No areas found'}
                </p>
              ) : (
                filteredAreas.map((a) => {
                  const isChecked = selectedAreaIds.includes(a.area_id.toString());
                  return (
                    <label
                      key={a.area_id}
                      className={`ap-area-picker__item ${isChecked ? 'is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleAreaToggle(a.area_id)}
                        className="ap-checkbox"
                      />
                      <div className="ap-area-picker__item-info">
                        <span className="ap-area-picker__item-name">{a.area_name}</span>
                        <span className="ap-area-picker__item-type">
                          {a.area_type_description || ''}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Footer with actions */}
        <div className="ap-form__footer">
          {/* Delete button (edit mode only, gated by canManageGroups) —
              opens the page's typed confirmation. */}
          {isEditMode && canManageGroups ? (
            <button
              type="button"
              onClick={handleDeleteClick}
              className="ap-btn ap-btn--danger"
            >
              <i className="fe fe-trash-2" aria-hidden="true" />
              Delete
            </button>
          ) : null}

          {/* Spacer pushes Cancel/Save right */}
          <div style={{ flex: 1 }} />

          <button
            type="button"
            onClick={onClose}
            className="ap-btn ap-btn--cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || saving}
            className="ap-btn ap-btn--primary"
          >
            {saving ? (
              <span className="ap-saving-text">
                <svg className="ap-icon-md ap-spinner" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </span>
            ) : (
              isEditMode ? 'Save Changes' : 'Create Group'
            )}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default AreaGroupDrawer;
